import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { FluentProvider, type Theme } from '@fluentui/react-components';
import { brands, isBrand, type Brand, type BrandDefinition } from './tokens';
import { brandAccent, getTheme, type Mode, type ModePreference } from './themes';

export const DEFAULT_MODE_STORAGE_KEY = 'gandhi-ui.mode';

export type BrandContextValue = {
  brand: Brand;
  definition: BrandDefinition;
  /** What was asked for, including `system`. */
  mode: ModePreference;
  /** What is actually rendered, after resolving `system`. */
  resolvedMode: Mode;
  theme: Theme;
  /** The brand accent at the resolved mode, for chrome Fluent's theme does not reach. */
  accent: string;
  setMode: (mode: ModePreference) => void;
  setBrand: (brand: Brand) => void;
};

const BrandContext = createContext<BrandContextValue | null>(null);

export function useBrand(): BrandContextValue {
  const value = useContext(BrandContext);
  if (!value) {
    throw new Error('useBrand must be used inside a <BrandProvider>.');
  }
  return value;
}

/** Context without the throw, for components that also accept an explicit `brand` prop. */
export function useBrandOptional(): BrandContextValue | null {
  return useContext(BrandContext);
}

/** An explicit `brand` prop wins; otherwise the surrounding provider. Never guesses. */
export function useResolvedBrand(brand: Brand | undefined, component: string): Brand {
  const context = useBrandOptional();
  const resolved = brand ?? context?.brand;
  if (!resolved) {
    throw new Error(`<${component}> needs a \`brand\` prop or a surrounding <BrandProvider>.`);
  }
  return resolved;
}

const DARK_QUERY = '(prefers-color-scheme: dark)';

const subscribeToSystem = (onChange: () => void) => {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const list = window.matchMedia(DARK_QUERY);
  list.addEventListener('change', onChange);
  return () => list.removeEventListener('change', onChange);
};

const readSystem = (): Mode => {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
};

/** Live `prefers-color-scheme`. Exported so apps can label a "System" menu item. */
export function useSystemMode(): Mode {
  return useSyncExternalStore(subscribeToSystem, readSystem, () => 'light' as const);
}

const isModePreference = (value: unknown): value is ModePreference =>
  value === 'light' || value === 'dark' || value === 'system';

const readStoredMode = (key: string): ModePreference | undefined => {
  try {
    const raw = window.localStorage.getItem(key);
    return isModePreference(raw) ? raw : undefined;
  } catch {
    return undefined; // private browsing, disabled storage
  }
};

export type BrandProviderProps = {
  brand: Brand;
  /** Initial preference. Changing it later overrides the persisted value. */
  mode?: ModePreference;
  /** Set false to keep the choice in memory only. */
  persistMode?: boolean;
  storageKey?: string;
  /** Rendered into a `<div>` rather than replacing the document body styling. */
  applyStylesToPortals?: boolean;
  className?: string;
  children?: ReactNode;
};

export function BrandProvider({
  brand,
  mode = 'system',
  persistMode = true,
  storageKey = DEFAULT_MODE_STORAGE_KEY,
  applyStylesToPortals = true,
  className,
  children,
}: BrandProviderProps) {
  const [brandState, setBrandState] = useState<Brand>(brand);
  const [modeState, setModeState] = useState<ModePreference>(
    () => (persistMode ? readStoredMode(storageKey) : undefined) ?? mode,
  );

  const previousModeProp = useRef(mode);
  useEffect(() => {
    if (previousModeProp.current !== mode) {
      previousModeProp.current = mode;
      setModeState(mode);
    }
  }, [mode]);

  useEffect(() => {
    setBrandState(brand);
  }, [brand]);

  const setMode = useCallback(
    (next: ModePreference) => {
      setModeState(next);
      if (!persistMode) return;
      try {
        window.localStorage.setItem(storageKey, next);
      } catch {
        // storage unavailable; the in-memory choice still applies
      }
    },
    [persistMode, storageKey],
  );

  const systemMode = useSystemMode();
  const resolvedMode: Mode = modeState === 'system' ? systemMode : modeState;
  const theme = getTheme(brandState, resolvedMode);

  // Tells the browser which way the page reads, so the canvas behind the app, form
  // controls and scrollbars match the theme instead of staying light.
  useEffect(() => {
    document.documentElement.style.colorScheme = resolvedMode;
  }, [resolvedMode]);

  const value = useMemo<BrandContextValue>(
    () => ({
      brand: brandState,
      definition: brands[brandState],
      mode: modeState,
      resolvedMode,
      theme,
      accent: brandAccent(brandState, resolvedMode),
      setMode,
      setBrand: setBrandState,
    }),
    [brandState, modeState, resolvedMode, theme, setMode],
  );

  return (
    <BrandContext.Provider value={value}>
      <FluentProvider
        theme={theme}
        applyStylesToPortals={applyStylesToPortals}
        className={className}
        data-brand={brandState}
        data-mode={resolvedMode}
      >
        {children}
      </FluentProvider>
    </BrandContext.Provider>
  );
}

export { isBrand };
